import React, { useEffect, useMemo, useRef, useState } from "react";
import { callable } from "../../../firebase.js";
import { useFirestoreCollection } from "../../../lib/useFirestore.js";
import { Crumbs, Back } from "./nav.jsx";
import { pdfPageCount, includedCaptureCounts } from "../lib/pagecount.js";
import { captureEmbedReady } from "../lib/cameras.js";

/**
 * Screen 5 (v1) — Proof. Ivin's note 2026-06-12: real first pages BEFORE
 * anything downloads or sends. Slice 3 makes it real:
 *
 *   • gate on embedsReady — until every confirmed capture's 800px print images
 *     are uploaded by the bridge, show "preparing print images N/M"
 *   • buildSheetProof → rasterized first overview page + first detail page
 *   • Approve → buildContactSheet → live Download PDF (Storage URL)
 *
 * "Send to job" still belongs to slice 4 (the push), so it stays disabled.
 */
export default function Proof({ nav, go }) {
  const { jobId, jobLabel } = nav;
  const { data: groups } = useFirestoreCollection(jobId ? `jobs/${jobId}/captureSheet` : null);

  // Confirmed captures with picks — the set the PDF includes.
  const { confirmedCaps, embedsReadyCount, pageEstimate } = useMemo(() => {
    const confirmed = groups.filter((g) => g.isConfirmed);
    const caps = confirmed.flatMap((g) => (g.captures || []).filter((c) => (c.selected || []).length > 0));
    return {
      confirmedCaps: caps,
      // D1: ready means embedsReady AND the signature still matches the current
      // picks — a stale boolean after a re-pick doesn't count.
      embedsReadyCount: caps.filter(captureEmbedReady).length,
      // C4: estimate over the builder's exact inclusion set, not raw captureCount.
      pageEstimate: pdfPageCount(includedCaptureCounts(groups)),
    };
  }, [groups]);
  const total = confirmedCaps.length;
  const embedsReady = total > 0 && embedsReadyCount >= total;

  const [proof, setProof] = useState({ loading: false, overviewUrl: null, detailUrl: null, error: null });
  const [build, setBuild] = useState({ building: false, downloadUrl: null, filename: null, error: null });
  const requestedRef = useRef(false);

  // Auto-build the proof once the print images are ready (once per mount).
  useEffect(() => {
    if (!jobId || !embedsReady || requestedRef.current) return;
    requestedRef.current = true;
    setProof((p) => ({ ...p, loading: true, error: null }));
    callable("buildSheetProof")({ jobId })
      .then((res) => {
        const d = res.data || {};
        setProof({ loading: false, overviewUrl: d.overviewUrl, detailUrl: d.detailUrl, error: null });
      })
      .catch((err) => setProof({ loading: false, overviewUrl: null, detailUrl: null, error: err.message || String(err) }));
  }, [jobId, embedsReady]);

  const approve = () => {
    setBuild({ building: true, downloadUrl: null, filename: null, error: null });
    callable("buildContactSheet")({ jobId })
      .then((res) => {
        const d = res.data || {};
        setBuild({ building: false, downloadUrl: d.downloadUrl, filename: d.filename, error: null });
      })
      .catch((err) => setBuild({ building: false, downloadUrl: null, filename: null, error: err.message || String(err) }));
  };

  return (
    <>
      <Crumbs
        trail={[
          { label: "Sheets", screen: "home", patch: { jobId: null } },
          { label: jobLabel, screen: "job" },
          { label: "Capture Sheet", screen: "groups" },
          { label: "Proof" },
        ]}
        go={go}
      />
      <Back label="Groups" onClick={() => go("groups")} />
      <div className="qhead">Does it look right?<span className="d">First pages, rendered for real — nothing downloads or sends until you approve · ≈ {pageEstimate} pages</span></div>

      {!embedsReady ? (
        <div className="proofnote">Preparing print images {embedsReadyCount}/{total} — the studio machine is uploading the high-res photos for your picks. The proof renders here as soon as they're ready.</div>
      ) : proof.loading ? (
        <div className="empty">Rendering the first pages…</div>
      ) : proof.error ? (
        <div className="proofnote" style={{ color: "#9A1B1B" }}>Couldn't build the proof: {proof.error}</div>
      ) : (
        <div className="proofwrap">
          <div className="proofstack">
            {proof.overviewUrl && <img className="proofpage" src={proof.overviewUrl} alt="Overview — first page" style={{ width: "100%", height: "auto" }} />}
            <div className="prooflabel">Overview — first page</div>
          </div>
          <div className="proofstack">
            {proof.detailUrl && <img className="proofpage" src={proof.detailUrl} alt="Capture detail — your 6 picks" style={{ width: "100%", height: "auto" }} />}
            <div className="prooflabel">Capture detail — your 6 picks</div>
          </div>
        </div>
      )}

      {build.downloadUrl ? (
        <div className="card" style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14 }}>
          <span style={{ fontSize: 15 }}>✅</span>
          <span><b>Contact sheet built.</b> <a href={build.downloadUrl} target="_blank" rel="noreferrer" className="btnP" style={{ padding: "4px 12px", fontSize: 12, marginLeft: 6, textDecoration: "none" }}>Download PDF</a>
            <div style={{ fontSize: 11, color: "var(--s-muted)", marginTop: 4 }}>{build.filename}</div>
          </span>
        </div>
      ) : build.error ? (
        <div className="proofnote" style={{ color: "#9A1B1B", marginTop: 12 }}>Build failed: {build.error}</div>
      ) : null}

      <div style={{ display: "flex", gap: 8, marginTop: 14, alignItems: "center" }}>
        <button type="button" className="btnP" disabled={!embedsReady || proof.loading || build.building || !!build.downloadUrl} onClick={approve}>
          {build.building ? "Building PDF…" : "Approve — build the contact sheet"}
        </button>
        <button type="button" className="btnS" onClick={() => go("send")} disabled={!build.downloadUrl} title={build.downloadUrl ? "" : "Approve the proof first"}>Continue to send →</button>
        <button type="button" className="btnS" onClick={() => go("groups")}>Back to picks</button>
      </div>
    </>
  );
}
